import { createContext, useContext } from "react"

const STORAGE_KEY = "debug:sidebarStyle"

export type SidebarStyleKey = "darkNavy" | "tealBrand" | "charcoal" | "light"

export const SIDEBAR_STYLE_OPTIONS: { value: SidebarStyleKey; label: string }[] = [
  { value: "darkNavy", label: "Dark Navy" },
  { value: "tealBrand", label: "Teal Brand (default)" },
  { value: "charcoal", label: "Charcoal" },
  { value: "light", label: "Light" },
]

export type SidebarColorTokens = {
  bg: string
  bgHover: string
  bgSelected: string
  bgSelectedHover: string
  text: string
  textMuted: string
  textDimmed: string
  border: string
  /** Close/icon button color */
  iconColor: string
  iconMuted: string
  /** "New Order" button */
  buttonBg: string
  buttonBgHover: string
  buttonText: string
  /** Count chip when not selected */
  chipBg: string
  chipText: string
  /** Count chip when selected */
  chipSelectedBg: string
  chipSelectedText: string
  /** Toggle button group */
  toggleColor: string
  toggleBorder: string
  toggleSelectedBg: string
  toggleSelectedColor: string
  /** Search field override — null means use default (white bg) */
  searchBg: string
  searchBorder: string
  /** Search icon button */
  searchBtnBg: string
  searchBtnBgHover: string
  searchBtnColor: string
  /** Date picker needs special treatment on dark bgs */
  datePickerBg: string
  datePickerTextColor: string
  datePickerIconColor: string
  datePickerBorderColor: string
}

const darkNavy: SidebarColorTokens = {
  bg: "#1e2235",
  bgHover: "#262b42",
  bgSelected: "#3d6cf5",
  bgSelectedHover: "#3d6cf5",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.85)",
  textDimmed: "rgba(255,255,255,0.7)",
  border: "rgba(255,255,255,0.1)",
  iconColor: "#ffffff",
  iconMuted: "rgba(255,255,255,0.7)",
  buttonBg: "#3d6cf5",
  buttonBgHover: "#3260e0",
  buttonText: "#ffffff",
  chipBg: "#3d6cf5",
  chipText: "#ffffff",
  chipSelectedBg: "rgba(255,255,255,0.25)",
  chipSelectedText: "#ffffff",
  toggleColor: "rgba(255,255,255,0.6)",
  toggleBorder: "rgba(255,255,255,0.2)",
  toggleSelectedBg: "rgba(255,255,255,0.15)",
  toggleSelectedColor: "#ffffff",
  searchBg: "#ffffff",
  searchBorder: "#bdbdbd",
  searchBtnBg: "#3d6cf5",
  searchBtnBgHover: "#3260e0",
  searchBtnColor: "#ffffff",
  datePickerBg: "#ffffff",
  datePickerTextColor: "#212121",
  datePickerIconColor: "#757575",
  datePickerBorderColor: "#bdbdbd",
}

const tealBrand: SidebarColorTokens = {
  bg: "#065A6D",
  bgHover: "#07697E",
  bgSelected: "#0B7285",
  bgSelectedHover: "#0B7285",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.9)",
  textDimmed: "rgba(255,255,255,0.7)",
  border: "rgba(255,255,255,0.12)",
  iconColor: "#ffffff",
  iconMuted: "rgba(255,255,255,0.7)",
  buttonBg: "#F08C6A",
  buttonBgHover: "#D9734F",
  buttonText: "#ffffff",
  chipBg: "rgba(255,255,255,0.18)",
  chipText: "#ffffff",
  chipSelectedBg: "rgba(255,255,255,0.3)",
  chipSelectedText: "#ffffff",
  toggleColor: "rgba(255,255,255,0.6)",
  toggleBorder: "rgba(255,255,255,0.2)",
  toggleSelectedBg: "rgba(255,255,255,0.18)",
  toggleSelectedColor: "#ffffff",
  searchBg: "#ffffff",
  searchBorder: "#bdbdbd",
  searchBtnBg: "#F08C6A",
  searchBtnBgHover: "#D9734F",
  searchBtnColor: "#ffffff",
  datePickerBg: "#ffffff",
  datePickerTextColor: "#212121",
  datePickerIconColor: "#757575",
  datePickerBorderColor: "#bdbdbd",
}

const charcoal: SidebarColorTokens = {
  bg: "#2A2D35",
  bgHover: "#33363F",
  bgSelected: "#4A4D58",
  bgSelectedHover: "#4A4D58",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.85)",
  textDimmed: "rgba(255,255,255,0.6)",
  border: "rgba(255,255,255,0.08)",
  iconColor: "#ffffff",
  iconMuted: "rgba(255,255,255,0.6)",
  buttonBg: "#5C6BC0",
  buttonBgHover: "#4A5AB0",
  buttonText: "#ffffff",
  chipBg: "rgba(255,255,255,0.12)",
  chipText: "#ffffff",
  chipSelectedBg: "rgba(255,255,255,0.25)",
  chipSelectedText: "#ffffff",
  toggleColor: "rgba(255,255,255,0.5)",
  toggleBorder: "rgba(255,255,255,0.15)",
  toggleSelectedBg: "rgba(255,255,255,0.12)",
  toggleSelectedColor: "#ffffff",
  searchBg: "#ffffff",
  searchBorder: "#bdbdbd",
  searchBtnBg: "#5C6BC0",
  searchBtnBgHover: "#4A5AB0",
  searchBtnColor: "#ffffff",
  datePickerBg: "#ffffff",
  datePickerTextColor: "#212121",
  datePickerIconColor: "#757575",
  datePickerBorderColor: "#bdbdbd",
}

const light: SidebarColorTokens = {
  bg: "#F7FAFA",
  bgHover: "#EEF3F4",
  bgSelected: "#0B7285",
  bgSelectedHover: "#065A6D",
  text: "#1A2A35",
  textMuted: "#465B63",
  textDimmed: "#8DA0A6",
  border: "#DCE4E6",
  iconColor: "#1A2A35",
  iconMuted: "#8DA0A6",
  buttonBg: "#0B7285",
  buttonBgHover: "#065A6D",
  buttonText: "#ffffff",
  chipBg: "#E8F6F8",
  chipText: "#0B7285",
  chipSelectedBg: "rgba(255,255,255,0.3)",
  chipSelectedText: "#ffffff",
  toggleColor: "#8DA0A6",
  toggleBorder: "#DCE4E6",
  toggleSelectedBg: "#0B7285",
  toggleSelectedColor: "#ffffff",
  searchBg: "#ffffff",
  searchBorder: "#DCE4E6",
  searchBtnBg: "#0B7285",
  searchBtnBgHover: "#065A6D",
  searchBtnColor: "#ffffff",
  datePickerBg: "#ffffff",
  datePickerTextColor: "#1A2A35",
  datePickerIconColor: "#8DA0A6",
  datePickerBorderColor: "#DCE4E6",
}

const styleMap: Record<SidebarStyleKey, SidebarColorTokens> = {
  darkNavy,
  tealBrand,
  charcoal,
  light,
}

export const getSidebarTokens = (key: SidebarStyleKey): SidebarColorTokens =>
  styleMap[key]

export type SidebarStyleContextValue = {
  style: SidebarStyleKey
  setStyle: (s: SidebarStyleKey) => void
  colors: SidebarColorTokens
}

export const SidebarStyleContext = createContext<SidebarStyleContextValue>({
  style: "tealBrand",
  setStyle: () => {},
  colors: tealBrand,
})

export const useSidebarStyle = () => useContext(SidebarStyleContext)

export const getInitialSidebarStyle = (): SidebarStyleKey => {
  const stored = localStorage.getItem(STORAGE_KEY) as SidebarStyleKey | null
  if (stored && stored in styleMap) return stored
  return "tealBrand"
}

export const persistSidebarStyle = (s: SidebarStyleKey) => {
  localStorage.setItem(STORAGE_KEY, s)
}
